// Shared helpers for task and project status evaluation.

const isValidDate = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime());

const toDate = (value) => {
  if (!value) return null;
  if (isValidDate(value)) return value;
  if (typeof value?.toDate === "function") {
    const converted = value.toDate();
    return isValidDate(converted) ? converted : null;
  }
  const parsed = new Date(value);
  return isValidDate(parsed) ? parsed : null;
};

const startOfDay = (date) => {
  if (!isValidDate(date)) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const DUBAI_TIMEZONE = "Asia/Dubai";
const ATTENTION_TO_OVERDUE_HOUR = 17; // 5 PM
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const getZonedParts = (date, timeZone) => {
  if (!isValidDate(date)) return null;
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts = formatter.formatToParts(date).reduce((acc, part) => {
      if (part.type !== "literal") {
        acc[part.type] = parseInt(part.value, 10);
      }
      return acc;
    }, {});

    const { year, month, day, hour = 0, minute = 0, second = 0 } = parts;
    if (
      [year, month, day].some(
        (value) => typeof value !== "number" || Number.isNaN(value)
      )
    ) {
      return null;
    }
    return { year, month, day, hour, minute, second };
  } catch (e) {
    return null;
  }
};

const toZonedDate = (date, timeZone) => {
  const parts = getZonedParts(date, timeZone);
  if (!parts) return null;
  const { year, month, day, hour, minute, second } = parts;
  return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
};

const startOfZonedDay = (date, timeZone) => {
  const parts = getZonedParts(date, timeZone);
  if (!parts) return null;
  const { year, month, day } = parts;
  return new Date(Date.UTC(year, month - 1, day));
};

export const evaluateTasks = (tasks = [], now = new Date()) => {
  const nowDate = toDate(now) || new Date();
  const nowInDubai = toZonedDate(nowDate, DUBAI_TIMEZONE);
  const startToday = startOfZonedDay(nowDate, DUBAI_TIMEZONE);
  const startTomorrow = startToday ? new Date(startToday.getTime() + ONE_DAY_MS) : null;
  const attentionCutoffToday = startToday
    ? new Date(startToday.getTime() + ATTENTION_TO_OVERDUE_HOUR * 60 * 60 * 1000)
    : null;

  let allTasksCompleted = true;
  let hasOverdue = false;
  let hasAttention = false;
  let lastCompletedDate = null;

  const updatedTasks = tasks.map((task) => {
    let newTaskStatus = "In Progress";
    const dueDate = toDate(task.dueDate);
    const dueDateOnly = startOfZonedDay(dueDate, DUBAI_TIMEZONE);
    const completedDate = toDate(task.completedOn);

    if (completedDate) {
      newTaskStatus = "Completed";
      if (!lastCompletedDate || completedDate > lastCompletedDate) {
        lastCompletedDate = completedDate;
      }
    } else {
      allTasksCompleted = false;
      if (dueDateOnly && startToday) {
        const isPast = dueDateOnly < startToday;
        const isToday = dueDateOnly.getTime() === startToday.getTime();
        const isTomorrow =
          startTomorrow && dueDateOnly.getTime() === startTomorrow.getTime();

        if (isPast) {
          newTaskStatus = "Overdue";
          hasOverdue = true;
        } else if (isToday) {
          const pastCutoff =
            attentionCutoffToday &&
            nowInDubai &&
            nowInDubai.getTime() >= attentionCutoffToday.getTime();
          if (pastCutoff) {
            newTaskStatus = "Overdue";
            hasOverdue = true;
          } else {
            newTaskStatus = "Attention";
            hasAttention = true;
          }
        } else if (isTomorrow) {
          newTaskStatus = "Attention"; // tomorrow
          hasAttention = true;
        }
      }
    }

    return { ...task, taskStatus: newTaskStatus };
  });

  return {
    updatedTasks,
    allTasksCompleted,
    hasOverdue,
    hasAttention,
    lastCompletedDate,
  };
};

export const determineProjectStatus = (
  { paymentStatus, invoiceRaised, allTasksCompleted, lastCompletedDate, hasOverdue, hasAttention },
  now = new Date()
) => {
  if (paymentStatus === "Paid") return "Paid";
  if (invoiceRaised === "Yes") return "Invoiced";

  if (allTasksCompleted) {
    const reference = toDate(now) || new Date();
    const daysSinceLastCompleted = lastCompletedDate
      ? (reference - lastCompletedDate) / (1000 * 60 * 60 * 24)
      : 0;
    if (daysSinceLastCompleted >= 10) return "To be invoiced";
    return "Completed";
  }

  if (hasOverdue) return "Overdue";
  if (hasAttention) return "Attention";
  return "In Progress";
};

export const isInvoiceDateSet = (dateValue) => {
  if (!dateValue) return false;
  const date =
    typeof dateValue?.toDate === "function"
      ? dateValue.toDate()
      : dateValue?.seconds
      ? new Date(dateValue.seconds * 1000)
      : new Date(dateValue);
  return !isNaN(date.getTime());
};

export const deriveInvoiceRaised = (dateValue, currentValue = "No") => {
  return isInvoiceDateSet(dateValue) ? "Yes" : "No";
};

export const derivePaymentStatus = (
  paymentStatus,
  invoiceRaised,
  paidOn
) => {
  const normalizedPaymentStatus =
    typeof paymentStatus === "string"
      ? paymentStatus.trim()
      : paymentStatus || "";

  if (isInvoiceDateSet(paidOn)) {
    return "Paid";
  }

  // Clearing the paid date after it was set should move the status back to Pending
  if (normalizedPaymentStatus === "Paid") {
    return "Pending";
  }

  if (
    invoiceRaised === "Yes" &&
    normalizedPaymentStatus !== "Pending" &&
    normalizedPaymentStatus !== "Paid"
  ) {
    return "Pending";
  }

  return normalizedPaymentStatus || "";
};

export const evaluateProjectStatuses = (project, now = new Date()) => {
  const {
    updatedTasks,
    allTasksCompleted,
    hasOverdue,
    hasAttention,
    lastCompletedDate,
  } = evaluateTasks(project.tasks || [], now);

  const derivedInvoiceRaised = deriveInvoiceRaised(
    project.dateForInvoice,
    project.invoiceRaised
  );

  const derivedPaymentStatus = derivePaymentStatus(
    project.paymentStatus,
    derivedInvoiceRaised,
    project.paidOn
  );

  const projectStatus = determineProjectStatus(
    {
      paymentStatus: derivedPaymentStatus,
      invoiceRaised: derivedInvoiceRaised,
      allTasksCompleted,
      lastCompletedDate,
      hasOverdue,
      hasAttention,
    },
    now
  );

  return {
    updatedTasks,
    projectStatus,
    allTasksCompleted,
    hasOverdue,
    hasAttention,
    lastCompletedDate,
    invoiceRaised: derivedInvoiceRaised,
    paymentStatus: derivedPaymentStatus,
  };
};
